import React, { useCallback, useState, useEffect, useContext, useMemo } from 'react';
import _ from 'lodash';
import {
    WizardPage,
    wizardProps
} from '@xengage/gw-portals-wizard-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useTranslator } from '@jutro/locale';
import { Claim, fnolCommonMessages } from 'gw-capability-fnol-common-react';
import { ClaimUtil, FNOLLossLocation } from 'gwce-capability-fnol-common-react-ext';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { withRouter } from 'react-router-dom';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { ProductUtil } from 'wnice-portals-util-react';
import PropertyIncidents from './PropertyIncidents/PropertyIncidents';
import metadata from './FNOLPAOtherLossDetailPage.metadata.json5';
import Pedestrians from './Pedestrians/Pedestrians';

const {
    PA_LOB_NAME,
    RT_LOB_NAME,
    CA_LOB_NAME
} = ProductUtil;

function FNOLPAOtherLossDetailPage(props) {
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        wizardData: claimVM,
        updateWizardData,
        updateWizardSnapshot,
        authHeader,
        isReadOnly = false
    } = props;
    const {
        loadingMask: { setLoadingMask },
    } = useDependencies('loadingMask');
    const { FNOLService } = useDependencies('FNOLService');
    const translator = useTranslator();
    const [showErrors, setShowErrors] = useState(false);
    const [isEditing, updateIsEditing] = useState(false);
    const [involvedValue, updateInvolvedValue] = useState(false);
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLPAOtherLossDetailPage');

    const LOB_NAME = ClaimUtil.getLobByPolicyType(_.get(claimVM, 'value.policy.policyType', 'personalAuto'));

    const {
        lobs: {
            [LOB_NAME]: {
                initalVehicleIncidentData_Ext: initalIncidentData,
                propertyOwnerContacts_Ext: propertyOwnerContacts = [],
                thirdPartyIncidents_Ext: thirdPartyIncidents = [],
                injuryIncidents_Ext: pedestrianIncidents = []
            }
        },
        lossCause,
        claimNumber
    } = claimVM.value;

    const vehicleOwnerContactsIndex = useMemo(() => {
        return _.findIndex(thirdPartyIncidents, elt => !_.isNil(elt.vehicleOwnerContacts_Ext))
    }, [thirdPartyIncidents]);

    const vehicleOwnerContacts = useMemo(() => {
        return _.get(thirdPartyIncidents, `${vehicleOwnerContactsIndex}.vehicleOwnerContacts_Ext`)
    }, [thirdPartyIncidents, vehicleOwnerContactsIndex]);

    const getInitPropertyOwnerContact = useCallback(() => {
        if (!_.isEmpty(vehicleOwnerContacts)) {
            // for PA FNOL, vehicle owner contacts has always been up to date
            return vehicleOwnerContacts
        }
        return propertyOwnerContacts
    }, [propertyOwnerContacts, vehicleOwnerContacts])

    const relatedContacts = _.get(initalIncidentData, 'contactMap', []);

    const thirdPartyVehicles = thirdPartyIncidents?.map((v) => v.vehicle);

    const propertyIncidentsPath = `lobs.${LOB_NAME}.fixedPropertyIncidents_Ext`;
    const propertyIncidents = _.get(claimVM.value, propertyIncidentsPath, []);

    const thirdPartyIncidentsPath = `lobs.${LOB_NAME}.thirdPartyIncidents_Ext`;
    const injuryIncidentsPath = `lobs.${LOB_NAME}.injuryIncidents_Ext`;

    const incidentsAddresses = _.get(claimVM.value, 'incidentsAddresses', []);
    const predefinedLossLocations = _.get(claimVM.value, 'predefinedLossLocations');

    useEffect(() => {
        const involved = !_.isEmpty(thirdPartyIncidents);
        updateInvolvedValue(involved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isVMValid = useCallback((vm) => {
        if (!vm) {
            return true;
        }
        return vm.aspects?.valid && vm.aspects?.subtreeValid;
    }, [])

    const isPageValid = useCallback(() => {
        const thirdPartyIncidentsVM = _.get(claimVM, thirdPartyIncidentsPath);
        const propertyIncidentsVM = _.get(claimVM, propertyIncidentsPath);
        const pedestrianIncidentsVM = _.get(claimVM, injuryIncidentsPath);
        return isVMValid(thirdPartyIncidentsVM) && isVMValid(propertyIncidentsVM) && isVMValid(pedestrianIncidentsVM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [claimVM]);

    useEffect(() => {
        registerInitialComponentValidation(isPageValid);
    }, [registerInitialComponentValidation, isPageValid]);

    const syncWizardData = useCallback(
        (data, basePath) => {
            const newClaimVM = viewModelService.clone(claimVM);
            _.set(newClaimVM.value, `lobs.${LOB_NAME}.${basePath}_Ext`, data);
            if (!isReadOnly) {
                updateWizardData(newClaimVM);
            }
        },[LOB_NAME, claimVM, isReadOnly, updateWizardData, viewModelService]
    )

    const savePedestrianIncident = useCallback(async (pedestrianIncident) => {
        setLoadingMask(true);
        const response = await FNOLService.savePedestrianIncident(
            claimNumber,
            pedestrianIncident,
            authHeader
        );
        setLoadingMask(false);
        if (!isReadOnly) {
            const newClaimVM = viewModelService.clone(claimVM);
            newClaimVM.value = response;
            updateWizardData(newClaimVM);
            updateWizardSnapshot(newClaimVM);
        }
    }, [setLoadingMask, FNOLService, claimNumber, authHeader, isReadOnly, viewModelService, claimVM, updateWizardData, updateWizardSnapshot])

    const removePedestrianIncident = useCallback(async (pedestrianIncident) => {
        setLoadingMask(true);
        const response = await FNOLService.removePedestrianIncident(
            claimNumber,
            pedestrianIncident.publicID,
            authHeader
        );
        setLoadingMask(false);
        if (!isReadOnly) {
            const newClaimVM = viewModelService.clone(claimVM);
            newClaimVM.value = response;
            updateWizardData(newClaimVM);
            updateWizardSnapshot(newClaimVM);
        }
    },[FNOLService, authHeader, claimNumber, claimVM, isReadOnly, setLoadingMask, updateWizardData, updateWizardSnapshot, viewModelService])

    const savePropertyIncident = useCallback(async (propertyIncident) => {
        setLoadingMask(true);
        const response = await FNOLService.savePropertyIncident(
            claimNumber,
            propertyIncident,
            authHeader
        );
        setLoadingMask(false);
        if (!isReadOnly) {
            const newClaimVM = viewModelService.clone(claimVM);
            newClaimVM.value = response;
            updateWizardData(newClaimVM);
            updateWizardSnapshot(newClaimVM);
        }
    }, [setLoadingMask, FNOLService, claimNumber, authHeader, isReadOnly, viewModelService, claimVM, updateWizardData, updateWizardSnapshot])

    const removePropertyIncident = useCallback(async (propertyIncident) => {
        setLoadingMask(true);
        const response = await FNOLService.removePropertyIncident(
            claimNumber,
            propertyIncident.publicID,
            authHeader
        );
        setLoadingMask(false);
        if (!isReadOnly) {
            const newClaimVM = viewModelService.clone(claimVM);
            newClaimVM.value = response;
            updateWizardData(newClaimVM);
            updateWizardSnapshot(newClaimVM);
        }
    },[FNOLService, authHeader, claimNumber, claimVM, isReadOnly, setLoadingMask, updateWizardData, updateWizardSnapshot, viewModelService])

    const overrides = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        otherVehiclesSection: {
            visible: [ PA_LOB_NAME, CA_LOB_NAME ].includes(LOB_NAME)
        },
        vehicleQuestionToggle: {
            disabled: !_.isEmpty(thirdPartyIncidents),
            value: involvedValue,
            onValueChange: (val) => updateInvolvedValue(val),
        },
        thirdPartyVehicleContainer: {
            visible: involvedValue,
            ...props,
            claimVM,
            LOB_NAME,
            syncWizardData,
            updateIsEditing,
            isEditing,
            basePath: 'vehicles',
            tableData: thirdPartyVehicles,
            incidentType: 'third_party'
        },
        paPropertyTitle: {
            visible: lossCause === 'collision_EXT'
        },
        propertyIncidents: {
            propertyIncidents: propertyIncidents,
            predefinedLossLocations,
            incidentsAddresses,
            LOB_NAME,
            visible: lossCause === 'collision_EXT',
            propertyOwnerContacts: [],
            savePropertyIncident,
            removePropertyIncident
        },
        pedestriansSection: {
            visible: [ PA_LOB_NAME, CA_LOB_NAME, RT_LOB_NAME ].includes(LOB_NAME)
        },
        paPedestrianTitle: {
            visible: lossCause === 'collision_EXT'
        },
        pedestrians: {
            pedestrianIncidents,
            relatedContacts,
            savePedestrianIncident,
            removePedestrianIncident,
            visible: lossCause === 'collision_EXT'
        }
    };

    const handleSaveClaimData = useCallback(() => {
        setLoadingMask(true);
        return FNOLService.saveFNOLDetails(claimVM.value, authHeader)
            .then((response) => {
                claimVM.value = response;
                return claimVM;
            })
            .finally(() => {
                setLoadingMask(false);
            });
    }, [FNOLService, authHeader, claimVM, setLoadingMask]);

    const onNext = useCallback(async () => {
        setShowErrors(false);
        if (!isComponentValid) {
            setShowErrors(true);
        }
        setLoadingMask(true);
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (isClaimNumberAvailable) {
            return handleSaveClaimData();
        }
        // eslint-disable-next-line no-return-await
        return await FNOLService.getFNOLDetails(policyNumber, claimVM.value, authHeader)
            .then((response) => {
                claimVM.value = new Claim(response);
                updateWizardData(claimVM)
                return claimVM;
            })
            .finally(() => {
                setLoadingMask(false);
            });
    }, [FNOLService, authHeader, claimVM, handleSaveClaimData, isComponentValid, setLoadingMask, updateWizardData]);

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
        },
        [claimVM]
    );

    const vehicleValid = useMemo(()=>{
        const {invalidChildProperties} = claimVM.aspects
        const ignoreList = ['Number', 'city', 'state']
        if(invalidChildProperties?.length > 0){
            if(invalidChildProperties.findIndex((ip) => ip.includes('thirdPartyIncidents_Ext') && 
                // ignore vehicleOwnerContacts_Ext validation CEI-6854 contacts may miss primary phone type cause phonenumber invalid
                ignoreList.findIndex((keyword) => ip.includes(keyword)) === -1) !== -1){
                return false
            }
        }
        return true
    },[claimVM.aspects])

    const resolvers = {
        resolveComponentMap: {
            losslocationcomponent: FNOLLossLocation,
            PropertyIncidents: PropertyIncidents,
            Pedestrians: Pedestrians
        }
    };

    return (
        <WizardPage
            skipWhen={initialValidation}
            disableNext={!isComponentValid || !vehicleValid}
            onNext={onNext}
            cancelLabel={translator(fnolCommonMessages.fnolSaveandExit)}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                resolveValue={readValue}
                overrideProps={overrides}
                onValidationChange={onValidate}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showErrors}
            />
        </WizardPage>
    );
}

FNOLPAOtherLossDetailPage.propTypes = wizardProps;
FNOLPAOtherLossDetailPage.propTypes = {
};
export default withRouter(withAuthenticationContext(FNOLPAOtherLossDetailPage));
